import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import './Login.css';
import { Eye, EyeOff, Mail } from 'lucide-react';



const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const { login, verifyOTP, resendOTP } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setTempEmail(result.email || email);
        try {
          await resendOTP(result.email || email);
        } catch (err) {
          console.error('Failed to send OTP:', err);
        }
      } else if (result.success) {
        const userRole = result.user?.role;
        if (userRole === 'Administrateur') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyOTP(tempEmail, twoFactorCode);

      if (result.success) {
        const userRole = result.user?.role;
        if (userRole === 'Administrateur') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setResendLoading(true);

    try {
      await resendOTP(tempEmail);
      setTwoFactorCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);

    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotMessage('Email de réinitialisation envoyé avec succès !');
      setForgotEmail('');
    } catch (err) {
      setForgotMessage(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  if (requiresTwoFactor) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Authentification à deux facteurs</h1>
          <p>Entrez le code de vérification envoyé à votre adresse email</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleTwoFactorSubmit}>
            <div className="form-group">
              <label htmlFor="twoFactorCode">Code de vérification</label>
              <input
                type="text"
                id="twoFactorCode"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="123456"
                required
                autoComplete="one-time-code"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </form>

          <button
            className="btn-link"
            onClick={handleResendOTP}
            disabled={resendLoading}
          >
            {resendLoading ? 'Renvoi en cours...' : 'Renvoyer le code'}
          </button>

          <button
            className="btn-link"
            onClick={() => {
              setRequiresTwoFactor(false);
              setTwoFactorCode('');
            }}
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Se connecter</h1>
        <p>Bienvenue ! Veuillez entrer vos coordonnées.</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Entrez votre email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? <Eye /> : <EyeOff />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>

        <div className="login-footer">
          <p>Vous n'avez pas de compte ? <Link to="/register">S'inscrire</Link></p>
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => setShowForgotPassword(true)}
          >
            Mot de passe oublié ?
          </button>
        </div>
      </div>

      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Réinitialiser le mot de passe</h3>
              <button
                className="close-btn"
                onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Entrez votre email pour recevoir un lien de réinitialisation.</p>
              <div className="form-group">
                <label htmlFor="forgotEmail">Email</label>
                <div className="password-input-wrapper">
                  <input
                    type="email"
                    id="forgotEmail"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Entrez votre email"
                    required
                  />
                  <Mail className="mail-icon" />
                </div>
              </div>
              {forgotMessage && <div className={`forgot-message ${forgotMessage.includes('succès') ? 'success' : 'error'}`}>{forgotMessage}</div>}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}
              >
                Annuler
              </button>
              <button
                className="btn-primary"
                onClick={handleForgotPassword}
                disabled={forgotLoading || !forgotEmail.trim()}
              >
                {forgotLoading ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
